"use client";
import * as React from "react";
import { Box, Button, FormControl, FormHelperText, TextField } from "@mui/material";
import Grid from "@mui/material/Grid2";

import { Controller, useForm } from "react-hook-form";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { useRouter } from "next/navigation";
import { createNewsletter } from "@/services/backOffice";
import { AxiosError } from "axios";

interface OtpComp {
  emailAddress: string;
  firstName: string;
  lastName: string;
  homeAirport?: string;
  mobileNumber: string;
  status: boolean;
  onBack: () => void;
  onSubmit: () => void;
}
interface OTP {
  otp: string;
}

const otpSchema = yup.object().shape({
  otp: yup.string().required("OTP is Required to subscribe"),
});

const OtpNewslater: React.FC<OtpComp> = ({
  emailAddress,
  firstName,
  lastName,
  homeAirport,
  mobileNumber,
  status,
  onBack,
  onSubmit,
}) => {
  const { handleSubmit, control, watch, reset } = useForm<OTP>({
    defaultValues: { otp: undefined },
    resolver: yupResolver(otpSchema),
    mode: "all",
  });
  const router = useRouter();
  const value = watch();
  const [error, setError] = React.useState<string | null>(null);

  const submitEnquiry = async (): Promise<void> => {
    try {
      await createNewsletter({
        email: emailAddress,
        firstName: firstName,
        lastName: lastName,
        countryOrLocation: homeAirport,
        mobile: mobileNumber,
        status: status,
        otp: value.otp,
        siteCode: "whv",
      });
      onSubmit();
      setError(null);
      reset();
      router.push("/thank-you");
    } catch (err) {
      const error = err as AxiosError<{ detail?: string }>;
      if (error.response) {
        const errorMessage = error.response.data?.detail;
        setError(errorMessage || "An unexpected error occurred.");
      } else {
        setError("Something went wrong. Please try again.");
      }
    }
  };
  return (
    <Box>
      <form onSubmit={handleSubmit(submitEnquiry)}>
        <Box sx={{ mb: { xs: "8px", md: "16px" }, fontSize: { xs: "14px", md: "16px" } }}>
          An OTP has been sent to your email. Enter it to verify your identity.
        </Box>
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, md: 7, lg: 6 }} order={1}>
            <FormControl variant="outlined" fullWidth>
              <Controller
                control={control}
                name={"otp"}
                render={({ field, fieldState: { error, invalid } }) => (
                  <>
                    <TextField
                      {...field}
                      sx={{ maxHeight: "2.8rem" }}
                      label="OTP"
                      color="secondary"
                      error={invalid}
                    />
                    <FormHelperText error sx={{ mt: "10px" }}>
                      {error?.message}
                    </FormHelperText>
                  </>
                )}
              />
            </FormControl>
          </Grid>
          <Grid size={{ xs: 6, md: 2.5, lg: 3 }} order={{ xs: 2, md: 1 }}>
            <Button
              variant="contained"
              type="submit"
              disableElevation
              sx={{
                color: "#fff",
                width: "100%",
                height: "3.5rem",
              }}
            >
              Submit
            </Button>
          </Grid>
          <Grid size={{ xs: 6, md: 2.5, lg: 3 }} order={{ xs: 1, md: 2 }}>
            <Button
              variant="contained"
              onClick={onBack}
              disableElevation
              sx={{
                background: "#bfbdff",
                border: "1px solid #060273",
                color: "#060273",
                width: "100%",
                height: "3.5rem",
              }}
            >
              Back
            </Button>
          </Grid>
        </Grid>
      </form>
      {error && <Box sx={{ color: "red", fontSize: "14px", p: "8px" }}>{error}</Box>}
    </Box>
  );
};

export default OtpNewslater;
